import styled from "@emotion/styled";
import type { ReactNode } from "react";

import { IconButton } from "../buttons/Buttons";
import {
  BUTTON_HEIGHT,
  ellipsise,
  FONT_WEIGHT_SEMI_BOLD,
  PROPERTY_PANEL_POPOVER_WIDTH,
  transientOptions,
} from "../utilities";

const CloseButton = styled(IconButton)`
  height: ${BUTTON_HEIGHT}px;
  width: ${BUTTON_HEIGHT}px;
  color: inherit;
  margin-left: 12px;
  font-size: 14px;
`;

const Container = styled("div", transientOptions)<{ $panel: "properties" | "context" }>`
  position: relative;
  &:after {
    content: "";
    position: absolute;
    top: 50%;
    ${({ $panel }) => `${$panel === "properties" ? "left: 100%" : "right: 100%"}`};
    margin-top: -4px;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    ${({ $panel, theme }) => `border-${$panel === "properties" ? "left" : "right"}: 8px solid ${theme.surface};`}
  }
`;

const OverflowContainer = styled.div`
  width: ${PROPERTY_PANEL_POPOVER_WIDTH}px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  background: ${({ theme }) => theme.surfaceVariant};
  height: 40px;
  padding: 0 6px 0 15px;
  font-size: ${({ theme }) => theme.fontSize};
`;

const HeaderText = styled.div`
  ${ellipsise};
`;

const ContentContainer = styled.div`
  background: ${({ theme }) => theme.surface};
  font-size: ${({ theme }) => theme.fontSizeSmall};
  padding: 12px 15px;
`;

type Props = {
  children?: ReactNode;
  onClose: () => void;
  header?: ReactNode;
  className?: string;
  panel?: "properties" | "context";
};

export const PropertiesPanelPopoverContent = ({
  header,
  children,
  onClose,
  className,
  panel = "properties",
}: Props) => (
  <Container className={className} $panel={panel}>
    <OverflowContainer>
      {header && (
        <Header>
          <HeaderText>{header}</HeaderText>
          <CloseButton onClick={onClose} icon="times" data-test="close-popover-button" />
        </Header>
      )}
      <ContentContainer>{children}</ContentContainer>
    </OverflowContainer>
  </Container>
);
